import { useMemo, useState } from 'react';
import { ApiError } from '../api/client.ts';
import {
  useEditFacility, useEditReasons, useFacilityVersions, useTypeChangePreview,
  type AddressCandidate, type FacilityEdit, type FacilityPageData, type TypeChangePreview,
} from '../api/facilityPage.ts';
import { useResidenceTypes } from '../api/places.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { EarlierVersions, EditedMarker } from './EarlierVersions.tsx';
import type { EditLike } from '../earlierVersions.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

type Facility = FacilityPageData['facility'];
type Text = Exclude<keyof FacilityEdit, 'keep_old_name' | 'distinct_from' | 'reason'>;

function startFrom(f: Facility): FacilityEdit {
  return {
    facility_name: f.facility_name,
    facility_type: f.facility_type ?? '',
    street_address: f.street_address ?? '',
    city: f.city ?? '',
    state: f.state ?? '',
    zip: f.zip ?? '',
    county: f.county ?? '',
    neighbourhood: f.neighbourhood ?? '',
    phone: f.phone ?? '',
    contact_email: f.contact_email ?? '',
    notes: f.notes ?? '',
    reason: '',
    reason_text: '',
    keep_old_name: false,
    distinct_from: [],
  };
}

const ADDRESS: Text[] = ['street_address', 'city', 'state', 'zip'];

/**
 * Edit a building's details (123): every field at once, a reason, and a new
 * version kept. For correcting the SAME building: if the people placed here
 * moved, each is moved, never by editing where they were.
 *
 * A type change shows what it does before Save is enabled (the visiting
 * standard, the placements re-labelled, what stops it). An address another
 * building holds is answered as at add: a different building, or leave it for
 * the merge that comes next.
 */
export function FacilityEditor({ facility, onDone }: { facility: Facility; onDone: (saved: boolean) => void }) {
  const [form, setForm] = useState<FacilityEdit>(() => startFrom(facility));
  const [candidates, setCandidates] = useState<AddressCandidate[] | null>(null);
  const reasons = useEditReasons();
  const types = useResidenceTypes('facility');
  const save = useEditFacility(facility.id);

  const set = (k: Text) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const typeChanges = form.facility_type !== (facility.facility_type ?? '') && form.facility_type !== '';
  const preview = useTypeChangePreview(facility.id, typeChanges ? form.facility_type : null);
  const renamed = form.facility_name.trim() !== facility.facility_name;
  const addressChanged = ADDRESS.some((k) => form[k].trim() !== (facility[k as keyof Facility] ?? ''));
  const reason = reasons.data?.reasons.find((r) => r.code === form.reason);
  const changed = useMemo(() => {
    const start = startFrom(facility);
    return (Object.keys(start) as (keyof FacilityEdit)[])
      .filter((k) => !['reason', 'reason_text', 'keep_old_name', 'distinct_from'].includes(k))
      .some((k) => String(form[k]).trim() !== String(start[k]).trim());
  }, [form, facility]);

  const p = preview.data?.preview;
  const typeBlocked = typeChanges && (!p || p.stopping.length > 0 || p.no_new_default || p.placements !== p.placements_visible);
  const ready = changed && !!form.facility_name.trim() && !!form.facility_type && !!reason
    && (!reason.needs_text || !!form.reason_text.trim())
    && (form.facility_type !== 'Other' || !!form.notes.trim())
    && !typeBlocked && !save.isPending;

  function submit(e: React.FormEvent, distinct: string[] = form.distinct_from) {
    e.preventDefault();
    const body = { ...form, distinct_from: distinct, keep_old_name: renamed && form.reason === 'UPDATED_INFORMATION' && form.keep_old_name };
    save.mutate(body, {
      onSuccess: () => onDone(true),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409 && (err.body as { candidates?: AddressCandidate[] })?.candidates) {
          setCandidates((err.body as { candidates: AddressCandidate[] }).candidates);
        }
      },
    });
  }

  return (
    <form onSubmit={(e) => submit(e)} className="space-y-4 border-b border-line px-4 py-4 lg:px-5" aria-labelledby="edit-heading">
      <h2 id="edit-heading" className="text-base font-bold text-neutral-900">Edit details</h2>
      <p className="rounded-lg border-l-8 border-warning-edge bg-warning-bg px-3 py-2 text-warning-fg">
        Correct this building&rsquo;s details. If the people placed here moved to a different building, record a move
        for each of them instead: an edit changes where the record says they always were.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={label} htmlFor="fe-name">Name</label>
          <input id="fe-name" className={field} value={form.facility_name} maxLength={160} onChange={set('facility_name')} />
        </div>
        <div className="md:col-span-2">
          <label className={label} htmlFor="fe-street">Street address</label>
          <input id="fe-street" className={field} value={form.street_address} maxLength={160} onChange={set('street_address')} />
          <p className={hint}>A unit or suite goes here, after the street: a building has no separate unit.</p>
        </div>
        <div>
          <label className={label} htmlFor="fe-city">City</label>
          <input id="fe-city" className={field} value={form.city} maxLength={96} onChange={set('city')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label} htmlFor="fe-state">State</label>
            <input id="fe-state" className={field} value={form.state} maxLength={32} onChange={set('state')} />
          </div>
          <div>
            <label className={label} htmlFor="fe-zip">ZIP</label>
            <input id="fe-zip" className={field} value={form.zip} maxLength={12} inputMode="numeric" onChange={set('zip')} />
          </div>
        </div>
        <div>
          <label className={label} htmlFor="fe-county">County</label>
          <input id="fe-county" className={field} value={form.county} maxLength={64} onChange={set('county')} />
        </div>
        <div>
          <label className={label} htmlFor="fe-hood">Neighbourhood</label>
          <input id="fe-hood" className={field} value={form.neighbourhood} maxLength={96} onChange={set('neighbourhood')} />
          {facility.neighbourhood_source === 'lookup' && form.neighbourhood === (facility.neighbourhood ?? '') && (
            <p className={hint}>From the address lookup. Type over it to make it yours; the lookup never replaces yours.</p>
          )}
        </div>
        <div>
          <label className={label} htmlFor="fe-phone">Phone</label>
          <input id="fe-phone" className={field} value={form.phone} maxLength={32} inputMode="tel" onChange={set('phone')} />
        </div>
        <div>
          <label className={label} htmlFor="fe-email">Email</label>
          <input id="fe-email" type="email" className={field} value={form.contact_email} maxLength={254} onChange={set('contact_email')} />
        </div>
        <div className="md:col-span-2">
          <label className={label} htmlFor="fe-type">Type</label>
          <select id="fe-type" className={field} value={form.facility_type} onChange={set('facility_type')}>
            {!facility.facility_type && <option value="">Not recorded</option>}
            {(types.data?.residence_types ?? []).map((t) => (
              <option key={t.code} value={t.code}>{t.label}</option>
            ))}
          </select>
          <p className={hint}>A type is corrected here, as what the building has always been. It is not for a building that changed what it is.</p>
        </div>
        <div className="md:col-span-2">
          <label className={label} htmlFor="fe-notes">
            Notes <span className="font-normal text-neutral-500">{form.facility_type === 'Other' ? '— say what kind of place it is' : '— optional'}</span>
          </label>
          <textarea id="fe-notes" className={`${field} min-h-[72px] py-2`} value={form.notes} maxLength={4000} onChange={set('notes')} />
        </div>
      </div>

      {typeChanges && <TypeChangeEffects loading={preview.isLoading} preview={p} error={preview.error} />}
      {addressChanged && (
        <p className="text-neutral-800">
          The map position will be cleared and the new address looked up as soon as this is saved.
        </p>
      )}

      <fieldset>
        <legend className={label}>Why is it being changed?</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {(reasons.data?.reasons ?? []).map((r) => (
            <label key={r.code}
                   className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 px-3 has-[:checked]:border-neutral-900 has-[:checked]:bg-neutral-100">
              <input type="radio" name="fe-reason" className="size-5" value={r.code} checked={form.reason === r.code}
                     onChange={() => setForm((f) => ({ ...f, reason: r.code }))} />
              <span className="text-neutral-900">{r.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {reason && (
        <div>
          <label className={label} htmlFor="fe-why">
            In words <span className="font-normal text-neutral-500">{reason.needs_text ? '— required for Other' : '— optional'}</span>
          </label>
          <input id="fe-why" className={field} value={form.reason_text} maxLength={2000} onChange={set('reason_text')} />
        </div>
      )}
      {renamed && form.reason === 'UPDATED_INFORMATION' && (
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={form.keep_old_name}
                 onChange={(e) => setForm((f) => ({ ...f, keep_old_name: e.target.checked }))} />
          <span className="text-neutral-900">
            Keep &ldquo;{facility.facility_name}&rdquo; as another name it is known by, so a search for it still finds
            this building.
          </span>
        </label>
      )}

      {candidates && (
        <div role="alert" className="space-y-2 rounded-lg border-l-8 border-warning-edge bg-warning-bg px-3 py-3 text-warning-fg">
          <p className="font-semibold">Another building on record has this address.</p>
          <ul className="list-disc pl-5">
            {candidates.map((c) => (
              <li key={c.id}>
                {c.facility_name} · {[c.street_address, c.city, c.zip].filter(Boolean).join(', ')}
                {c.clients_here > 0 ? ` · ${c.clients_here} placed there` : ''}
              </li>
            ))}
          </ul>
          <p>
            If it is the same building, leave this one as it is: merging two records of one building comes in the
            next release. If it is a different building at the same address, say so and save.
          </p>
          <button type="button" className={secondary}
                  onClick={(e) => { const ids = candidates.map((c) => c.id); setCandidates(null); setForm((f) => ({ ...f, distinct_from: ids })); submit(e, ids); }}>
            They are different buildings: save
          </button>
        </div>
      )}
      {save.error && !candidates && <Refusal error={save.error} />}

      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready}>{save.isPending ? 'Saving…' : 'Save changes'}</button>
        <button type="button" className={secondary} onClick={() => onDone(false)}>Cancel</button>
      </div>
      {!changed && <p className={hint}>Nothing has been changed yet.</p>}
    </form>
  );
}

function standard(s: TypeChangePreview['standard_before']): string {
  return s ? `${s.visits_per_year} a year, every ${s.interval_days} days, ${s.grace_days} days' grace` : 'no standard recorded';
}

function TypeChangeEffects({ loading, preview: p, error }: { loading: boolean; preview: TypeChangePreview | undefined; error: Error | null }) {
  if (error) return <Refusal error={error} />;
  if (loading || !p) return <p className="text-neutral-700">Working out what the new type changes…</p>;
  const same = JSON.stringify(p.standard_before) === JSON.stringify(p.standard_after);
  const stopped = p.stopping.length > 0 || p.no_new_default || p.placements !== p.placements_visible;
  return (
    <div className={stopped
      ? 'rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900'
      : 'rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-neutral-900'}>
      <p className="font-semibold">What changing the type does</p>
      <ul className="mt-1 list-disc space-y-1 pl-5">
        <li>
          The visiting standard: {same ? `${standard(p.standard_after)}, no change` : `${standard(p.standard_before)} becomes ${standard(p.standard_after)}`}.
          {' '}It is read from the building&rsquo;s type for every placement here, past ones included.
        </li>
        <li>
          {p.placed_now} {p.placed_now === 1 ? 'client is' : 'clients are'} placed here now; {p.ever_placed} {p.ever_placed === 1 ? 'has' : 'have'} ever been.
        </li>
        {p.relabel.map((r) => (
          <li key={r.from_code}>{r.count} {r.count === 1 ? 'placement' : 'placements'} recorded as {r.from_label} will read {r.to_label}.</li>
        ))}
        {p.relabel.length === 0 && p.stopping.length === 0 && <li>No placement here needs re-labelling.</li>}
        {p.planned_return_after !== p.planned_return_before && (
          <li>The move form will {p.planned_return_after ? 'now offer' : 'no longer offer'} &ldquo;Planned return?&rdquo; for this building.</li>
        )}
      </ul>
      {p.stopping.length > 0 && (
        <p className="mt-2 font-semibold">
          It cannot be changed: {p.stopping.map((s) => `${s.count} ${s.count === 1 ? 'placement is' : 'placements are'} recorded as ${s.label}`).join('; ')},
          which is not this building&rsquo;s usual kind of placement. Someone decides what those placements were first.
        </p>
      )}
      {p.no_new_default && <p className="mt-2 font-semibold">It cannot be changed: the new type has no usual kind of placement to re-label to.</p>}
      {p.placements !== p.placements_visible && (
        <p className="mt-2 font-semibold">It cannot be changed by you: some placements here are on clients you cannot see.</p>
      )}
    </div>
  );
}

/**
 * An edited building (123): the page shows it as it stands; this adds the Edited marker and only its earlier versions
 * (19 October: the same pattern as visits), never the current values again. A notes change stays withheld here.
 */
export function FacilityEdits({ id, times }: { id: string; times: number }) {
  const versions = useFacilityVersions(id, times > 0);
  if (times === 0) return null;
  const edits: EditLike[] | undefined = versions.data?.edits.map((e) => ({
    version: e.version, by: e.edited_by, at: e.edited_at, reason: e.reason_label, reason_text: e.reason_text,
    notes: [
      ...(e.old_name_kept ? ['The old name was kept as another name it is known by.'] : []),
      ...(e.placements_relabelled > 0 ? [`${e.placements_relabelled} ${e.placements_relabelled === 1 ? 'placement' : 'placements'} re-labelled to the new type.`] : []),
    ],
    changes: e.changes,
  }));
  return (
    <div className="mt-1">
      <EditedMarker edits={edits} />
      <EarlierVersions edits={edits} loading={versions.isLoading} error={versions.error as Error | null}
                       withheldWords="changed (shown to those who may edit the building)" />
    </div>
  );
}
